import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { grabsApi } from "@/api/grabs";
import { musicApi } from "@/api/music";
import type { AlbumRelease, Track } from "@/api/types";
import { AlbumInteractiveSearchModal } from "@/components/AlbumInteractiveSearchModal";
import { BlocklistSection } from "@/components/BlocklistSection";
import { RedownloadButton } from "@/components/RedownloadButton";
import { TagPills } from "@/components/TagPills";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { DownloadedIndicator } from "./ArtistDetail";
import { PosterTile } from "./ArtistList";

export function AlbumDetail() {
  const { id } = useParams({ from: "/music/albums/$id" });
  const albumId = Number(id);
  const qc = useQueryClient();
  const { toast } = useToast();

  const albumQ = useQuery({
    queryKey: ["music", "album", albumId],
    queryFn: () => musicApi.getAlbum(albumId),
  });

  // The album endpoint doesn't include parent-artist data, so fire a second
  // query for the artist name once the album loads. We only need the name
  // to render the breadcrumb / page subtitle.
  const artistId = albumQ.data?.album.artist_id;
  const artistQ = useQuery({
    queryKey: ["music", "artist", artistId],
    queryFn: () => musicApi.getArtist(artistId!),
    enabled: artistId != null,
  });

  const grabsQ = useQuery({
    queryKey: ["grabs", "album", albumId],
    queryFn: () =>
      grabsApi.list({ media_kind: "album", media_id: albumId, limit: 25 }),
  });

  const monitorMut = useMutation({
    mutationFn: (monitored: boolean) =>
      musicApi.updateAlbum(albumId, { monitored }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["music", "album", albumId] });
      if (artistId != null)
        qc.invalidateQueries({ queryKey: ["music", "artist", artistId] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Update failed", "error"),
  });

  // Issue #7: per-album preferred release (edition). Pass empty string
  // to clear back to "no preference"; selector then treats the album as
  // release-group keyed (status quo).
  const preferredReleaseMut = useMutation({
    mutationFn: (mbid: string) =>
      musicApi.updateAlbum(albumId, { preferred_release_mbid: mbid }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["music", "album", albumId] });
      toast("Preferred release updated", "success");
    },
    onError: (err) =>
      toast(
        err instanceof Error ? err.message : "Failed to set preferred release",
        "error",
      ),
  });

  const searchMut = useMutation({
    mutationFn: () => musicApi.searchAlbum(albumId),
    onSuccess: (record) => {
      toast(
        `Grabbed ${record.release_title} via ${record.indexer_name ?? "?"} → ${record.download_client_name ?? "?"}`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["grabs"] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Grab failed", "error"),
  });

  // Issue #3: interactive picker modal — opens the candidate list
  // without dispatching, lets the user override the auto-pick.
  const [interactiveOpen, setInteractiveOpen] = useState(false);

  const sortedTracks = useMemo(() => {
    const tracks = albumQ.data?.tracks ?? [];
    return tracks.slice().sort((a, b) => a.track_number - b.track_number);
  }, [albumQ.data?.tracks]);

  if (albumQ.isLoading) {
    return <p className="text-sm text-foreground/60">Loading…</p>;
  }
  if (!albumQ.data) {
    return <p className="text-sm text-foreground/60">Not found.</p>;
  }

  const { album } = albumQ.data;
  const year = parseYear(album.release_date);
  const artistName = artistQ.data?.artist.name;

  const subtitleParts: string[] = [];
  if (artistName) subtitleParts.push(artistName);
  if (year != null) subtitleParts.push(String(year));
  if (album.primary_type) subtitleParts.push(album.primary_type);
  const subtitle =
    subtitleParts.length > 0 ? subtitleParts.join(" · ") : undefined;

  return (
    <div className="space-y-6">
      <div className="text-xs text-foreground/60">
        <Link
          to="/music/artists/$id"
          params={{ id: String(album.artist_id) }}
          className="hover:text-accent"
        >
          ← {artistName ?? "Back to artist"}
        </Link>
      </div>

      <PageHeader
        title={album.title}
        description={subtitle}
        actions={
          <>
            <Button
              variant="secondary"
              loading={monitorMut.isPending}
              onClick={() => monitorMut.mutate(!album.monitored)}
            >
              {album.monitored ? "Unmonitor" : "Monitor"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setInteractiveOpen(true)}
            >
              Search interactive
            </Button>
            <Button
              loading={searchMut.isPending}
              onClick={() => searchMut.mutate()}
            >
              Search & Grab
            </Button>
          </>
        }
      />
      <AlbumInteractiveSearchModal
        open={interactiveOpen}
        onClose={() => setInteractiveOpen(false)}
        albumId={albumId}
        albumTitle={
          artistName ? `${artistName} — ${album.title}` : album.title
        }
      />
      {album.disambiguation && (
        // MusicBrainz disambiguation — surfaced under the title to
        // distinguish e.g. "Greatest Hits" from "Greatest Hits
        // (deluxe edition)". Italic + muted to read as a subtitle
        // without competing with the page header.
        <p className="-mt-4 text-sm italic text-foreground/60">
          {album.disambiguation}
        </p>
      )}

      <TagPills target={{ kind: "media", mediaKind: "album", id: album.id }} showEmpty />

      <section className="flex flex-col gap-6 sm:grid sm:grid-cols-[10rem_1fr]">
        <div className="w-40 space-y-3 self-center sm:w-auto sm:self-auto">
          <div className="overflow-hidden rounded border border-line">
            <PosterTile
              kind="albums"
              id={album.id}
              url={album.poster_url}
              name={album.title}
              ratio="1/1"
            />
          </div>
          {/* Issue #12: disc art from fanart.tv. Round PNG with
           * transparency, displayed below the cover so it reads as a
           * companion artifact rather than competing for the primary
           * cover slot. NULL = nothing rendered. */}
          {album.disc_art_url && (
            <div className="flex justify-center">
              <img
                src={album.disc_art_url}
                alt=""
                aria-hidden="true"
                className="aspect-square w-32 rounded-full object-contain drop-shadow"
                loading="lazy"
              />
            </div>
          )}
        </div>
        <dl className="grid grid-cols-[10rem_1fr] gap-y-1 self-start text-xs text-foreground/70">
          <dt className="text-foreground/50">MusicBrainz ID</dt>
          <dd className="font-mono">{album.mbid}</dd>
          <dt className="text-foreground/50">Release date</dt>
          <dd>{album.release_date ?? "—"}</dd>
          <dt className="text-foreground/50">Type</dt>
          <dd>{album.primary_type ?? "—"}</dd>
          <dt className="text-foreground/50">Monitored</dt>
          <dd>{album.monitored ? "Yes" : "No"}</dd>
          <dt className="text-foreground/50">Has file</dt>
          <dd>{album.has_file ? "Yes" : "No"}</dd>
          <dt className="text-foreground/50">Path</dt>
          <dd className="font-mono">{album.path ?? "—"}</dd>
        </dl>
      </section>

      {/* Issue #10: muted footer reflecting the music RSS-sync
       * cursor. Only meaningful for monitored, unfiled albums (the
       * RSS loop's eligibility filter); for everything else we hide
       * the line so it doesn't read as "stale" on filed albums. */}
      {album.monitored && !album.has_file && (
        <RssSyncFooter lastRssSyncAt={album.last_rss_sync_at} />
      )}

      <ReleasesSection
        albumId={albumId}
        preferredMbid={album.preferred_release_mbid}
        onPick={(mbid) => preferredReleaseMut.mutate(mbid)}
        isSaving={preferredReleaseMut.isPending}
      />

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground/80">
          Tracks ({sortedTracks.length})
        </h2>
        {sortedTracks.length === 0 ? (
          <EmptyState
            title="No tracks"
            description="The MusicBrainz import didn't return any tracks for this album."
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="w-12 px-3 py-2 font-medium tabular-nums">#</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="w-20 px-3 py-2 font-medium tabular-nums">
                    Duration
                  </th>
                  <th className="w-16 px-3 py-2 font-medium">File</th>
                  <th className="w-12 px-3 py-2 font-medium">Lyrics</th>
                  <th className="px-3 py-2 font-medium">Path</th>
                </tr>
              </thead>
              <tbody>
                {sortedTracks.map((t) => (
                  <TrackRow key={t.id} track={t} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <BlocklistSection mediaKind="album" mediaId={albumId} />

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground/80">
          Recent grabs
        </h2>
        {grabsQ.isLoading ? (
          <p className="text-xs text-foreground/60">Loading…</p>
        ) : !grabsQ.data || grabsQ.data.length === 0 ? (
          <EmptyState
            title="No grabs yet"
            description="Click Search & Grab to query indexers and dispatch the best release."
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-xs">
              <thead className="bg-surface-2/50 text-left uppercase text-foreground/60">
                <tr>
                  <th className="px-3 py-2 font-medium">Release</th>
                  <th className="px-3 py-2 font-medium">Indexer</th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {grabsQ.data.map((g) => (
                  <tr key={g.id} className="border-t border-line">
                    <td className="px-3 py-2 font-mono">{g.release_title}</td>
                    <td className="px-3 py-2 text-foreground/70">
                      {g.indexer_name ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-foreground/70">
                      {g.download_client_name ?? "—"}
                    </td>
                    <td className="px-3 py-2 capitalize">{g.status}</td>
                    <td className="px-3 py-2 text-foreground/60">
                      {new Date(g.grabbed_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <RedownloadButton
                        grabId={g.id}
                        releaseTitle={g.release_title}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * One row in the tracks table. Extracted as its own component so the
 * lyrics expansion state stays scoped per-row instead of bubbling up
 * to the album-level useState (which would re-render the whole list
 * on every toggle).
 */
function TrackRow({ track }: { track: Track }) {
  const [expanded, setExpanded] = useState(false);
  const hasLyrics = !!track.has_lyrics;

  return (
    <>
      <tr className="border-t border-line">
        <td className="px-3 py-2 text-xs tabular-nums text-foreground/60">
          {track.track_number}
        </td>
        <td className="px-3 py-2 font-medium">{track.title}</td>
        <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
          {formatDuration(track.duration_ms)}
        </td>
        <td className="px-3 py-2">
          <DownloadedIndicator hasFile={track.has_file} path={track.path} />
        </td>
        <td className="px-3 py-2">
          {hasLyrics ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-base leading-none text-accent hover:text-accent/80"
              title={expanded ? "Hide lyrics" : "Show lyrics"}
              aria-label={expanded ? "Hide lyrics" : "Show lyrics"}
            >
              ♪
            </button>
          ) : (
            <span className="text-foreground/30">—</span>
          )}
        </td>
        <td className="px-3 py-2">
          {track.has_file && track.path ? (
            <span
              className="block max-w-md truncate font-mono text-[11px] text-foreground/60"
              title={track.path}
            >
              {track.path}
            </span>
          ) : (
            <span className="text-foreground/30">—</span>
          )}
        </td>
      </tr>
      {expanded && hasLyrics && (
        <tr className="border-t border-line bg-surface-2/30">
          <td colSpan={6} className="px-6 py-3">
            <LyricsPanel trackId={track.id} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Renders the `.lrc` content for a track. Fetches lazily — only on
 * first expand — and caches via React Query so re-collapsing and
 * re-expanding is instant. The body is rendered as `<pre>` so LRC
 * timing tags `[mm:ss.xx]` line up monospaced.
 */
function LyricsPanel({ trackId }: { trackId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["music", "track", trackId, "lyrics"],
    queryFn: () => musicApi.lyrics.get(trackId),
    staleTime: 5 * 60 * 1000,
  });
  if (isLoading) {
    return <p className="text-xs text-foreground/55">Loading lyrics…</p>;
  }
  if (error) {
    return (
      <p className="text-xs text-bad">
        {error instanceof Error ? error.message : "Failed to load lyrics."}
      </p>
    );
  }
  return (
    <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded border border-line bg-surface/50 px-3 py-2 font-mono text-[11px] leading-relaxed text-foreground/85">
      {data}
    </pre>
  );
}

/**
 * Issue #7: per-album release / edition picker. Lazy-loads the
 * candidate list from the backend (which itself lazy-loads from
 * MusicBrainz on first hit). When the user picks a row, persist via
 * PATCH `/api/v1/albums/:id`. An empty preference means "no preference"
 * — the selector falls back to the release-group-keyed flow.
 *
 * Selection is informational today: the importer + grab selector
 * don't yet consume `preferred_release_mbid`. Future work, documented
 * in `arr_music::releases`.
 */
function ReleasesSection({
  albumId,
  preferredMbid,
  onPick,
  isSaving,
}: {
  albumId: number;
  preferredMbid: string | null;
  onPick: (mbid: string) => void;
  isSaving: boolean;
}) {
  const releasesQ = useQuery({
    queryKey: ["music", "album", albumId, "releases"],
    queryFn: () => musicApi.listAlbumReleases(albumId),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-foreground/80">Releases</h2>
        {preferredMbid && (
          <button
            type="button"
            disabled={isSaving}
            onClick={() => onPick("")}
            className="text-xs text-foreground/60 hover:text-accent disabled:opacity-50"
          >
            Clear preference
          </button>
        )}
      </div>
      <p className="text-xs text-foreground/55">
        Pick a specific MusicBrainz release (Standard, Deluxe, Japanese
        edition, remaster, etc). The grab selector will treat your pick
        as informational for now.
      </p>
      {releasesQ.isLoading ? (
        <p className="text-xs text-foreground/60">Loading releases…</p>
      ) : releasesQ.error ? (
        <p className="text-xs text-bad">
          {releasesQ.error instanceof Error
            ? releasesQ.error.message
            : "Failed to load releases."}
        </p>
      ) : !releasesQ.data || releasesQ.data.length === 0 ? (
        <EmptyState
          title="No releases listed"
          description="MusicBrainz hasn't returned any releases for this release-group yet."
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-xs">
            <thead className="bg-surface-2/50 text-left uppercase text-foreground/60">
              <tr>
                <th className="w-10 px-3 py-2 font-medium" />
                <th className="px-3 py-2 font-medium">Edition</th>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Country</th>
                <th className="px-3 py-2 font-medium">Format</th>
                <th className="px-3 py-2 font-medium">Tracks</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Label</th>
              </tr>
            </thead>
            <tbody>
              {releasesQ.data.map((r) => (
                <ReleaseRow
                  key={r.mbid}
                  release={r}
                  selected={r.mbid === preferredMbid}
                  disabled={isSaving}
                  onSelect={() => onPick(r.mbid)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ReleaseRow({
  release,
  selected,
  disabled,
  onSelect,
}: {
  release: AlbumRelease;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const editionLabel = release.disambiguation
    ? release.disambiguation
    : (release.title ?? "—");
  return (
    <tr
      className={`border-t border-line ${
        selected ? "bg-accent/10" : "hover:bg-surface-2/30"
      }`}
    >
      <td className="px-3 py-2 align-top">
        <input
          type="radio"
          name="preferred-release"
          aria-label={`Select ${editionLabel}`}
          checked={selected}
          disabled={disabled}
          onChange={onSelect}
        />
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-medium text-foreground/85">{editionLabel}</div>
        <div className="font-mono text-[11px] text-foreground/50">
          {release.mbid}
        </div>
      </td>
      <td className="px-3 py-2 align-top text-foreground/70">
        {release.date ?? "—"}
      </td>
      <td className="px-3 py-2 align-top text-foreground/70">
        {release.country ?? "—"}
      </td>
      <td className="px-3 py-2 align-top text-foreground/70">
        {release.format ?? "—"}
      </td>
      <td className="px-3 py-2 align-top tabular-nums text-foreground/70">
        {release.track_count ?? "—"}
      </td>
      <td className="px-3 py-2 align-top text-foreground/70">
        {release.status ?? "—"}
      </td>
      <td className="px-3 py-2 align-top text-foreground/70">
        {release.label
          ? release.catalog_number
            ? `${release.label} · ${release.catalog_number}`
            : release.label
          : "—"}
      </td>
    </tr>
  );
}

function parseYear(date: string | null): number | null {
  if (!date) return null;
  const m = /^(\d{4})/.exec(date);
  return m ? Number(m[1]) : null;
}

/**
 * Issue #10: muted "RSS-checked Xh ago" footer for monitored, unfiled
 * albums. Reflects `albums.last_rss_sync_at`, the cursor stamped by
 * `arr_music::rss_sync` on every visit (success or failure). NULL =
 * "never RSS-checked" — the loop will pick the album up on its next
 * tick.
 *
 * Kept text-only (no button) so the page header stays uncluttered;
 * users wanting to force a sweep go through Settings → Music.
 */
function RssSyncFooter({
  lastRssSyncAt,
}: {
  lastRssSyncAt: string | null;
}) {
  const label = describeRssSync(lastRssSyncAt);
  return (
    <p className="text-[11px] text-foreground/45">
      RSS-checked {label}
    </p>
  );
}

/** Coerce an ISO-8601 timestamp into a humane "Nh ago" / "never". */
function describeRssSync(at: string | null): string {
  if (!at) return "never";
  const t = Date.parse(at);
  if (Number.isNaN(t)) return "never";
  const deltaMs = Date.now() - t;
  if (deltaMs < 0) return "just now";
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** "mm:ss" — null returns an em-dash. */
function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
